import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Avatar,
  AvatarGroup,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_AI_EVIDENCE_OWNERS_FOR_CHECK,
  REQUEST_AI_EVIDENCE_ACCESS,
} from "../../apis/integrated-evidence/aiEvidenceApis";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import ConfirmSnackbar from "../../components/Snackbar/ConfirmSnackbar";
import { initials } from "../../utils/aiEvidenceOwners";
import RequestReviewDialog from "../../components/RequestReviewDialog/RequestReviewDialog";
import {
  FEATURE_IDS,
  useFeatureEnabled,
} from "../../components/AccessControl";

const AIEvidenceAccessEmptyState = ({ onClearFilter }) => {
  const theme = useTheme();
  const tc = theme.customColors;
  const info = tc.status.info;
  const aiEvidenceEnabled = useFeatureEnabled(
    FEATURE_IDS.REGO_AI_EVIDENCE_COLLECTION
  );

  const { data } = useQuery(GET_AI_EVIDENCE_OWNERS_FOR_CHECK, {
    skip: !aiEvidenceEnabled,
    fetchPolicy: "cache-first",
  });
  const owners = data?.getAIEvidenceOwnersForCheck || [];
  const multiple = owners.length > 1;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const [requestAccess, { loading: sending }] = useMutation(
    REQUEST_AI_EVIDENCE_ACCESS,
  );

  const openDialog = () => {
    // A lone owner has no checkbox to tick, so it is selected implicitly.
    // With several, start empty so the choice is deliberate.
    setSelected(
      multiple ? new Set() : new Set(owners.map((o) => o.employeeDataId)),
    );
    setError(null);
    setDialogOpen(true);
  };

  const toggleOwner = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    setError(null);
    try {
      const res = await requestAccess({
        variables: { ownerIds: Array.from(selected) },
      });
      const result = res?.data?.requestAIEvidenceAccess;
      if (result?.success) {
        const names = owners
          .filter((o) => selected.has(o.employeeDataId))
          .map((o) => o.name || o.email)
          .filter(Boolean);
        const recipients =
          names.length === 1
            ? names[0]
            : names.length === 2
              ? `${names[0]} and ${names[1]}`
              : `${names[0]} and ${names.length - 1} others`;
        setDialogOpen(false);
        setSnackbarInfo({
          message: `Access request sent to ${recipients}`,
          vertical: "top",
          horizontal: "right",
          key: Date.now(),
        });
      } else {
        setError(result?.message || "Could not send the request.");
      }
    } catch (err) {
      setError("Could not send the request.");
    }
  };

  return (
    <>
      <Box
        sx={{
          maxWidth: 420,
          mx: "auto",
          py: 5,
          px: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Icon medallion: a locked sparkle — AI evidence behind an access gate */}
        <Box
          sx={{
            position: "relative",
            width: 52,
            height: 52,
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(info.main, 0.09),
            border: `1px solid ${alpha(info.main, 0.16)}`,
            mb: 2.25,
          }}
        >
          <AutoAwesomeOutlinedIcon
            sx={{ fontSize: 24, color: alpha(info.main, 0.85) }}
          />
          <Box
            sx={{
              position: "absolute",
              right: -6,
              bottom: -6,
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: tc.background.main,
              border: `1px solid ${tc.border.subtle}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockOutlinedIcon
              sx={{ fontSize: 12, color: tc.text.disabled }}
            />
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: tc.text.strong,
            letterSpacing: "-0.01em",
            mb: 0.75,
          }}
        >
          {owners.length === 0
            ? "No data owners yet"
            : "This evidence belongs to someone else"}
        </Typography>

        <Typography
          sx={{
            fontSize: "13px",
            lineHeight: 1.6,
            color: tc.text.secondary,
            maxWidth: 340,
            mb: owners.length > 0 ? 2.5 : 3,
          }}
        >
          {owners.length === 0
            ? "Once a data owner is assigned to your connected integrations, you can ask them for access here."
            : "Ask a data owner to share their integration so you can review it yourself, or ask them to review it for you."}
        </Typography>

        {owners.length > 0 && (
          <>
            {/* Who you'd be asking — makes the request concrete before the click */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2.5,
                px: 1.5,
                py: 0.75,
                borderRadius: "999px",
                border: `1px solid ${tc.border.subtle}`,
                bgcolor: tc.background.hover,
              }}
            >
              <AvatarGroup
                max={4}
                sx={{
                  "& .MuiAvatar-root": {
                    width: 22,
                    height: 22,
                    fontSize: "10px",
                    fontWeight: 600,
                    borderColor: tc.background.main,
                    bgcolor: alpha(info.main, 0.14),
                    color: info.text,
                  },
                }}
              >
                {owners.map((owner, index) => (
                  <Tooltip
                    key={owner.employeeDataId || index}
                    title={owner.name || owner.email || ""}
                  >
                    <Avatar>{initials(owner.name || owner.email)}</Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
              <Typography
                sx={{ fontSize: "12px", color: tc.text.secondary }}
              >
                {owners.length === 1
                  ? owners[0]?.name || owners[0]?.email
                  : `${owners.length} data owners`}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                disableElevation
                onClick={openDialog}
                sx={{
                  bgcolor: info.main,
                  color: tc.button.text,
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13.5px",
                  px: 2.5,
                  py: 0.875,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: info.text,
                    boxShadow: tc.shadow.filterChip,
                  },
                }}
              >
                Request access
              </Button>

              {/* The other way out: leave the evidence with its owner and ask
                  them to review it themselves. */}
              <Button
                variant="outlined"
                disableElevation
                onClick={() => setReviewOpen(true)}
                sx={{
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "13.5px",
                  px: 2.5,
                  py: 0.875,
                  backgroundColor: tc.background.main,
                  borderColor: tc.border.strong,
                  color: tc.text.strong,
                  "&:hover": {
                    borderColor: tc.text.secondary,
                    backgroundColor: tc.background.hover,
                  },
                }}
              >
                Ask them to review
              </Button>
            </Box>
          </>
        )}

        {onClearFilter && (
          <Box
            sx={{
              mt: 3,
              pt: 2.5,
              width: "100%",
              borderTop: `1px solid ${tc.border.subtle}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: "12.5px", color: tc.text.disabled }}>
              Looking for every control?
            </Typography>
            <Button
              variant="text"
              onClick={onClearFilter}
              sx={{
                fontSize: "12.5px",
                fontWeight: 500,
                color: info.text,
                textTransform: "none",
                p: 0,
                minWidth: 0,
                "&:hover": {
                  background: "none",
                  textDecoration: "underline",
                },
              }}
            >
              Clear the Rego AI filter
            </Button>
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={dialogOpen}
        onClose={() => !sending && setDialogOpen(false)}
        onConfirm={handleConfirm}
        loading={sending}
        confirmDisabled={selected.size === 0}
        heading="Request access"
        buttonText="Send request"
        note={error}
        content={
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                color: tc.text.secondary,
                mb: multiple ? 2 : 0,
              }}
            >
              {multiple
                ? "Choose who to ask. We'll email them asking to share their integration with you."
                : `${owners[0]?.name || owners[0]?.email} will be emailed the integration they need to grant you access to.`}
            </Typography>

            {multiple && (
              <Box
                sx={{
                  border: `1px solid ${tc.border.subtle}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {owners.map((owner, index) => (
                  <Box
                    key={owner.employeeDataId || index}
                    onClick={() => toggleOwner(owner.employeeDataId)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      borderBottom:
                        index < owners.length - 1
                          ? `1px solid ${tc.border.subtle}`
                          : "none",
                      "&:hover": { backgroundColor: tc.background.hover },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selected.has(owner.employeeDataId)}
                      sx={{ p: 0.5 }}
                    />
                    <Avatar
                      sx={{
                        width: 26,
                        height: 26,
                        fontSize: "11px",
                        fontWeight: 600,
                        bgcolor: alpha(info.main, 0.14),
                        color: info.text,
                      }}
                    >
                      {initials(owner.name || owner.email)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13.5px",
                          fontWeight: 500,
                          color: tc.text.strong,
                        }}
                      >
                        {owner.name || owner.email}
                      </Typography>
                      {owner.integrations && (
                        <Typography
                          sx={{ fontSize: "11.5px", color: tc.text.disabled }}
                        >
                          {owner.integrations}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        }
      />

      {/* No checkId: scoped to the owner's integrations, since Controls spans
          every check rather than naming one. */}
      <RequestReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default AIEvidenceAccessEmptyState;
